"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface XTrend {
  symbol: string;
  tweetCount: number;
  totalEngagement: number;
  totalWeight: number;
  uniqueAuthors: number;
  latestTweetAt: number;
  latestTweet: { title: string; url: string | null; source: string } | null;
  coverage: {
    onBinanceSwap: boolean;
    onDex: boolean;
    venues: string;
    kinds: string;
  };
}

const REFRESH_MS = 15_000;

function formatRel(ts: number): string {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h`;
}

function engagementBar(value: number, max: number): string {
  if (max <= 0) return "0%";
  return `${Math.min(100, (value / max) * 100).toFixed(1)}%`;
}

export function XTrends() {
  const [trends, setTrends] = useState<XTrend[]>([]);
  const [hours, setHours] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/x-trends?hours=${hours}&limit=50`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { trends: XTrend[]; ts: number };
      setTrends(body.trends);
      setUpdatedAt(body.ts);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      void load();
      timerRef.current = setTimeout(tick, REFRESH_MS);
    };
    tick();
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [load]);

  const maxEng = trends.reduce((m, t) => Math.max(m, t.totalEngagement), 0);

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-1 text-xs">
          {[1, 3, 6, 12, 24].map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => setHours(h)}
              className={
                "px-3 py-1.5 rounded-md border transition-colors "
                + (hours === h
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                  : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700")
              }
            >
              {h}h
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {updatedAt && (
            <span className="text-zinc-600">
              更新於 {new Date(updatedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
          >
            {loading ? "更新中…" : "↻ 立即刷新"}
          </button>
        </div>
      </div>

      {error && <p className="text-xs text-rose-400 mb-3">載入失敗：{error}</p>}

      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/80 text-xs text-zinc-500 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">代幣</th>
              <th className="px-4 py-3 text-right">貼文</th>
              <th className="px-4 py-3 text-right">互動</th>
              <th className="px-4 py-3 text-right">作者</th>
              <th className="px-4 py-3 text-left">覆蓋</th>
              <th className="px-4 py-3 text-left">最新貼文片段</th>
              <th className="px-4 py-3 text-right">最新</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {trends.map((t, i) => (
              <tr key={t.symbol} className="hover:bg-zinc-900/60">
                <td className="px-4 py-3 text-zinc-500 tabular-nums w-10">{i + 1}</td>
                <td className="px-4 py-3 font-semibold">{t.symbol}</td>
                <td className="px-4 py-3 text-right tabular-nums">{t.tweetCount}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex flex-col items-end">
                    <span className="tabular-nums text-zinc-200">
                      {Math.round(t.totalEngagement).toLocaleString()}
                    </span>
                    <div className="w-20 h-1 mt-0.5 bg-zinc-800 rounded overflow-hidden">
                      <div
                        className="h-full bg-emerald-400/60"
                        style={{ width: engagementBar(t.totalEngagement, maxEng) }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-400">
                  {t.uniqueAuthors}
                </td>
                <td className="px-4 py-3 text-xs">
                  {t.coverage.onBinanceSwap && (
                    <span className="inline-block px-1.5 py-0.5 mr-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-500/30">
                      Binance
                    </span>
                  )}
                  {t.coverage.onDex && (
                    <span className="inline-block px-1.5 py-0.5 mr-1 rounded bg-fuchsia-500/10 text-fuchsia-300 border border-fuchsia-500/30">
                      DEX
                    </span>
                  )}
                  {!t.coverage.onBinanceSwap && !t.coverage.onDex && (
                    <span className="text-zinc-600">未追蹤</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-zinc-400 max-w-md truncate">
                  {t.latestTweet?.url ? (
                    <a
                      href={t.latestTweet.url}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-emerald-300"
                    >
                      {t.latestTweet.title}
                    </a>
                  ) : (
                    t.latestTweet?.title ?? "—"
                  )}
                </td>
                <td className="px-4 py-3 text-right text-xs text-zinc-500 tabular-nums">
                  {formatRel(t.latestTweetAt)}
                </td>
              </tr>
            ))}
            {trends.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-zinc-500">
                  {loading ? (
                    "載入中…"
                  ) : (
                    <div className="space-y-2">
                      <p>近期{hours}h 內無社群資料</p>
                      <p className="text-xs text-zinc-600">
                        來源：CoinGecko 熱搜（免費）+ OpenTwitter（需 token）+ 新聞事件
                      </p>
                      <p className="text-xs text-zinc-600">
                        若 Discord 頻道長期都只有公告，代表 6551.io token 配額已用完 →
                        <a
                          href="https://6551.io/mcp"
                          target="_blank"
                          rel="noreferrer"
                          className="underline text-emerald-400 ml-1"
                        >
                          申請新 token
                        </a>
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
